import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

import API from '../../services/api';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';

const Contact = () => {
    const location = useLocation();

    // A single state object to hold all the form's input values
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        topic: '',
        message: '',
        terms: false,
    });
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [errors, setErrors] = useState({});
    const [submissionState, setSubmissionState] = useState({
        statusType: null,
        message: '',
        isError: false,
    });
    // State that holds personalized details for the success message
    const [submissionDetails, setSubmissionDetails] = useState({
        firstName: '',
        email: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const recaptchaRef = useRef(null);

    // This effect runs whenever the user navigates to this page
    // It ensures that any previous success or error messages are cleared
    useEffect(() => {
        setSubmissionState({ statusType: null, message: '', isError: false });
    }, [location]);

    const handleChange = (e) => {
        if (submissionState.statusType) {
            setSubmissionState({ statusType: null, message: '', isError: false });
        }
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Full Name is required.';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid.';
        }
        if (!formData.subject.trim()) newErrors.subject = 'Subject is required.';
        if (!formData.topic) newErrors.topic = 'Topic of Discussion is required.';
        if (!formData.message.trim()) newErrors.message = 'Message is required.';
        if (!formData.terms) newErrors.terms = 'You must agree to the terms.';
        if (!recaptchaToken) newErrors.recaptcha = 'Please complete the CAPTCHA.';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting || !validateForm()) return;

        setIsSubmitting(true);
        setSubmissionState({ statusType: null, message: '', isError: false });

        try {
            const payload = { ...formData, 'g-recaptcha-response': recaptchaToken };
            const response = await API.post('contact/messages/', payload);

            setSubmissionState({
                statusType: response.data.status,
                message: response.data.message, // This can be used for fallback messages
                isError: false,
            });

            // Saves the personalized details from the backend
            if (response.data.status === 'SUCCESS') {
                setSubmissionDetails({
                    firstName: response.data.firstName,
                    email: response.data.email,
                });
                setFormData({ name: '', email: '', subject: '', topic: '', message: '', terms: false });
            }
        } catch (error) {
            console.error("Form submission error:", error);
            const errorMessage = error.response?.data?.message ||
                (typeof error.response?.data === 'object' ? Object.values(error.response.data).flat().join(' ') : 'An unexpected error occurred.');
            setSubmissionState({
                statusType: 'ERROR',
                message: errorMessage,
                isError: true,
            });
        } finally {
            setIsSubmitting(false);
            recaptchaRef.current?.reset();
            setRecaptchaToken(null);
        }
    };

    return (
        <>
            <style>{`
                .checkbox-tick { display: flex; align-items: center; justify-content: center; }
                .checkbox-tick svg { opacity: 0; transition: opacity 0.2s ease-in-out; }
                .peer:checked + .checkbox-tick svg { opacity: 1; }
                .peer:checked + .checkbox-tick { background-color: #fff; }
                .peer:checked + .checkbox-tick svg { color: #000; }
                .dark .peer:checked + .checkbox-tick { background-color: #D9D9D9; }
                .dark .peer:checked + .checkbox-tick svg { color: #000; }
            `}</style>
            <div className="bg-white dark:bg-[#141414] text-black dark:text-[#D9D9D9] transition-colors duration-300 min-h-screen flex flex-col">
                <Navbar />
                <main className="flex-grow flex items-center justify-center py-4 px-8">
                    <div className="w-full max-w-md my-10">
                        {/* --- Page Header --- */}
                        <h1 className="text-2xl text-center text-black font-medium dark:text-gray-100 mt-8 mb-4">Contact Us</h1>
                        <p className="text-sm mt-2 text-center text-gray-600 dark:text-[#D9D9D9]">Get in touch with the Trueway team</p>

                        {/* --- Form Section --- */}
                        <form onSubmit={handleSubmit} className="mt-8 space-y-6" noValidate>
                            {/* --- Name Input --- */}
                            <div>
                                <label htmlFor="name" className="text-sm text-black dark:text-white">Full Name <span className="text-red-500">*</span></label>
                                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="mt-2 px-3 py-2 border rounded-lg text-sm w-full bg-white dark:bg-[#2E2E2E] text-black dark:text-white border-gray-300 dark:border-[#2A2A2A] focus:outline-none focus:border-[#38BDF8] dark:focus:border-[#7DD3FC] focus:ring-1 focus:ring-[#38BDF8] dark:focus:ring-[#7DD3FC]" placeholder="Enter your full name" />
                                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                            </div>

                            {/* --- Email Input --- */}
                            <div>
                                <label htmlFor="email" className="text-sm text-black dark:text-white">Email <span className="text-red-500">*</span></label>
                                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="mt-2 px-3 py-2 border rounded-lg text-sm w-full bg-white dark:bg-[#2E2E2E] text-black dark:text-white border-gray-300 dark:border-[#2A2A2A] focus:outline-none focus:border-[#38BDF8] dark:focus:border-[#7DD3FC] focus:ring-1 focus:ring-[#38BDF8] dark:focus:ring-[#7DD3FC]" placeholder="Enter your email" />
                                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                            </div>

                            {/* --- Subject Input --- */}
                            <div>
                                <label htmlFor="subject" className="text-sm text-black dark:text-white">Subject <span className="text-red-500">*</span></label>
                                <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} className="mt-2 px-3 py-2 border rounded-lg text-sm w-full bg-white dark:bg-[#2E2E2E] text-black dark:text-white border-gray-300 dark:border-[#2A2A2A] focus:outline-none focus:border-[#38BDF8] dark:focus:border-[#7DD3FC] focus:ring-1 focus:ring-[#38BDF8] dark:focus:ring-[#7DD3FC]" placeholder="Enter subject" />
                                {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                            </div>

                            {/* --- Topic Dropdown --- */}
                            <div>
                                <label htmlFor="topic" className="text-sm text-black dark:text-white">Topic of Discussion <span className="text-red-500">*</span></label>
                                <select id="topic" name="topic" value={formData.topic} onChange={handleChange} className="mt-2 px-3 py-2 border rounded-lg text-sm w-full bg-white dark:bg-[#2E2E2E] text-black dark:text-white border-gray-300 dark:border-[#2A2A2A] focus:outline-none focus:border-[#38BDF8] dark:focus:border-[#7DD3FC] focus:ring-1 focus:ring-[#38BDF8] dark:focus:ring-[#7DD3FC]">
                                    <option value="">Select a topic</option>
                                    <option value="bug">Bug Report</option>
                                    <option value="feature">Feature Request</option>
                                    <option value="investment">Investment Inquiry</option>
                                    <option value="news">News Inquiry</option>
                                    <option value="support">Support Request</option>
                                    <option value="others">Others</option>
                                </select>
                                {errors.topic && <p className="text-red-500 text-xs mt-1">{errors.topic}</p>}
                            </div>

                            {/* --- Message Textarea --- */}
                            <div>
                                <label htmlFor="message" className="text-sm text-black dark:text-white">Message <span className="text-red-500">*</span></label>
                                <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="5" className="mt-2 px-3 py-2 border rounded-lg text-sm w-full bg-white dark:bg-[#2E2E2E] text-black dark:text-white border-gray-300 dark:border-[#2A2A2A] focus:outline-none focus:border-[#38BDF8] dark:focus:border-[#7DD3FC] focus:ring-1 focus:ring-[#38BDF8] dark:focus:ring-[#7DD3FC]" placeholder="Enter your message"></textarea>
                                {errors.message && <p className="text-red-500 text-xs">{errors.message}</p>}
                            </div>

                            {/* --- reCAPTCHA --- */}
                            <div>
                                <div className="transform scale-75 origin-left">
                                    <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={(token) => setRecaptchaToken(token)} onExpired={() => setRecaptchaToken(null)} />
                                </div>
                                {errors.recaptcha && <p className="text-red-500 text-xs">{errors.recaptcha}</p>}
                            </div>

                            {/* --- Terms of Service Checkbox --- */}
                            <div className="flex flex-col">
                                <div className="flex items-center">
                                    <label className="flex items-center cursor-pointer">
                                        <input type="checkbox" id="terms" name="terms" checked={formData.terms} onChange={handleChange} className="hidden peer" />
                                        <span className="checkbox-tick w-5 h-5 bg-white dark:bg-[#2E2E2E] border border-gray-300 dark:border-[#2A2A2A] rounded-sm transition-all duration-300">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" /></svg>
                                        </span>
                                        <span className="ml-2 text-sm text-gray-700 dark:text-[#D9D9D9]">I agree to the <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-[#7DD3FC] hover:underline">Terms of Service</Link> and <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-[#7DD3FC] hover:underline">Privacy Policy</Link> <span className="text-red-500">*</span></span>
                                    </label>
                                </div>
                                {errors.terms && <p className="text-red-500 text-xs mt-1">{errors.terms}</p>}
                            </div>

                            {/* --- Submit Button --- */}
                            <button type="submit" disabled={isSubmitting} className="w-full px-4 py-2 rounded-lg text-sm bg-stone-200 dark:bg-stone-800 text-black dark:text-stone-100 hover:bg-stone-300 dark:hover:bg-stone-700 active:bg-stone-400 dark:active:bg-stone-600 dark:border dark:border-stone-600 disabled:opacity-50">
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>

                            {/* --- Submission Feedback --- */}
                            {submissionState.statusType && (
                                <div className="mt-4">
                                    {submissionState.statusType === 'SUCCESS' && (
                                        <div className="bg-amber-50 dark:bg-amber-900/10 p-6 rounded-lg border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-100">
                                            <div className="flex items-start gap-3">
                                                <svg className="w-5 h-5 text-amber-600 dark:text-amber-300 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                                <div>
                                                    <p className="font-medium">Hi {submissionDetails.firstName},</p>
                                                    <p className="mt-1 text-sm">Thanks for reaching out! We've received your message and our team will get back to you within 3-5 business days.</p>
                                                    <p className="mt-2 text-sm">You'll receive a copy of your message at <strong>{submissionDetails.email}</strong> for your reference. Look out for an email from us in your inbox. If you can't find it, it might be in your spam folder.</p>
                                                    <p className="mt-2 text-xs italic text-amber-600 dark:text-amber-300">The Trueway Team</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {submissionState.statusType === 'ERROR' && (
                                        <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-lg border border-red-200 dark:border-red-700 text-red-800 dark:text-red-100">
                                            <p>{submissionState.message}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </form>
                        <p className="mt-4 text-sm text-center text-gray-600 dark:text-[#D9D9D9]">For general inquiries, email us at <a href="mailto:contact@trueway.app" className="text-blue-600 dark:text-[#7DD3FC] hover:underline">contact@trueway.app</a>.</p>
                    </div>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default Contact;